import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { expenseUserPageShellSx } from "../ExpensesUser/expenseUserLayout";
import {
  pageContentCardSx,
  pageHeroShellSx,
  pageFieldSx,
  pagePrimaryButtonSx,
  pageSectionTitleSx,
  pageMobileCardSx,
} from "../../styles/pageModernStyles";
import {
  createTpvCaja,
  deleteTpvCaja,
  listTpvCajas,
} from "../../api/Tpv/tpvApi";
import { formatEur, formatTpvDate, toMoney } from "../../utils/tpvCalc";

export default function TpvCajasPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [importeIni, setImporteIni] = useState("0");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listTpvCajas();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || t("Tpv.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    const n = Number(String(importeIni).replace(",", "."));
    if (!Number.isFinite(n)) {
      setError(t("Tpv.importeNumeric"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const caja = await createTpvCaja(n);
      setOpen(false);
      navigate(`/tpv/${caja.id}`);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Tpv.createError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    setError("");
    try {
      await deleteTpvCaja(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Tpv.deleteCajaError"),
      );
    } finally {
      setDeleting(false);
    }
  };

  const openCount = rows.filter((r) => !r.cerrado).length;

  return (
    <Box sx={expenseUserPageShellSx(contentMargin, { maxWidth: "100%" })}>
      <Paper sx={pageHeroShellSx(theme)} elevation={0}>
        <Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 1.75, md: 2.25 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                <StorefrontOutlinedIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, lineHeight: 1.2 }}
                >
                  {t("Tpv.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("Tpv.subtitle")}
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end"
            >
              {!loading && rows.length ? (
                <Chip
                  size="small"
                  color={openCount ? "success" : "info"}
                  label={`${openCount} ${t("Tpv.open").toLowerCase()}`}
                  sx={{ fontWeight: 800 }}
                />
              ) : null}
              <Button
                variant="contained"
                startIcon={<AddOutlinedIcon />}
                onClick={() => {
                  setImporteIni("0");
                  setOpen(true);
                }}
                sx={pagePrimaryButtonSx(theme)}
              >
                {t("Tpv.openCaja")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={pageContentCardSx(theme)} elevation={0}>
          <Typography color="text.secondary">{t("Tpv.emptyCajas")}</Typography>
          <Button
            variant="contained"
            sx={{ mt: 2, ...pagePrimaryButtonSx(theme) }}
            startIcon={<AddOutlinedIcon />}
            onClick={() => setOpen(true)}
          >
            {t("Tpv.openCaja")}
          </Button>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {rows.map((row) => {
            const total = toMoney(
              Number(row.ingMetalico || 0) + Number(row.ingTarjeta || 0),
            );
            const canDelete = Number(row.ticketCount || 0) === 0;
            return (
              <Paper
                key={row.id}
                elevation={0}
                onClick={() => navigate(`/tpv/${row.id}`)}
                sx={{
                  ...pageMobileCardSx(theme, {
                    active: !row.cerrado,
                    accentColor: row.cerrado
                      ? theme.palette.text.secondary
                      : theme.palette.success.main,
                  }),
                  cursor: "pointer",
                  pl: 2.25,
                  "&:hover": {
                    borderColor: alpha(theme.palette.primary.main, 0.45),
                    boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.12)}`,
                  },
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
                        {t("Tpv.cajaN", { id: row.id })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {formatTpvDate(row.openedAt)}
                        {row.openedByName ? ` · ${row.openedByName}` : ""}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={row.cerrado ? "info" : "success"}
                      label={row.cerrado ? t("Tpv.closed") : t("Tpv.open")}
                      sx={{
                        fontWeight: 800,
                        ...(row.cerrado
                          ? {
                              bgcolor: alpha(theme.palette.info.main, 0.16),
                              color: theme.palette.info.dark,
                              border: `1px solid ${alpha(theme.palette.info.main, 0.45)}`,
                            }
                          : {
                              bgcolor: alpha(theme.palette.success.main, 0.18),
                              color: theme.palette.success.dark,
                              border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`,
                            }),
                      }}
                    />
                    {canDelete ? (
                      <IconButton
                        size="small"
                        aria-label={t("Tpv.deleteCaja")}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(row);
                        }}
                        sx={{ color: "error.main", mt: -0.25 }}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ mb: 0.25 }}
                      >
                        <PaymentsOutlinedIcon
                          sx={{ fontSize: 14, color: "success.main" }}
                        />
                        <Typography sx={{ ...pageSectionTitleSx, mb: 0 }}>
                          {t("Tpv.cash")}
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                        {formatEur(row.ingMetalico)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ mb: 0.25 }}
                      >
                        <CreditCardOutlinedIcon
                          sx={{ fontSize: 14, color: "info.main" }}
                        />
                        <Typography sx={{ ...pageSectionTitleSx, mb: 0 }}>
                          {t("Tpv.card")}
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                        {formatEur(row.ingTarjeta)}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      pt: 0.5,
                      borderTop: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.7),
                    }}
                  >
                    <Typography sx={{ ...pageSectionTitleSx, mb: 0 }}>
                      {t("Tpv.total")}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: "primary.main" }}>
                      {formatEur(total)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("Tpv.openCaja")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("Tpv.importeIni")}
            value={importeIni}
            onChange={(e) => setImporteIni(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
            fullWidth
            sx={pageFieldSx(theme)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("Tpv.cancel")}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {t("Tpv.add")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("Tpv.deleteCaja")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {t("Tpv.deleteCajaConfirm", { id: deleteTarget?.id })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t("Tpv.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {t("Tpv.deleteCaja")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
